import { useSubscription } from '../hooks/useSubscription';
import { redirectToSetupCheckout, redirectToSubscriptionCheckout } from '../services/stripeService';
import { Check, Zap } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@shared/components/ui/Toaster';

export function SubscriptionTiers() {
  const { tiers, currentTier, hasSetupFee, subscriptionStatus, loading } = useSubscription();
  const [processingTier, setProcessingTier] = useState<string | null>(null);

  // Check if user has active subscription
  const hasActiveSubscription = subscriptionStatus?.subscription_status === 'active';

  const handleSelectTier = async (tierSlug: string) => {
    try {
      setProcessingTier(tierSlug);

      if (!hasSetupFee) {
        // Step 1: Pay setup fee
        await redirectToSetupCheckout({ intendedTier: tierSlug });
      } else if (!hasActiveSubscription) {
        // Step 2: Subscribe after setup fee is paid
        await redirectToSubscriptionCheckout({
          tier: tierSlug,
          successUrl: `${window.location.origin}/dashboard?subscription=success`,
          cancelUrl: `${window.location.origin}/dashboard?subscription=cancel`,
        });
      } else {
        // Already have subscription, this would be a change
        toast.info('Use the billing portal to change your subscription');
        setProcessingTier(null);
      }
    } catch (err: any) {
      console.error('Failed to start checkout:', err);
      const errorMessage = err?.message || 'Failed to start checkout. Please try again.';

      if (errorMessage.includes('Stripe failed to initialize')) {
        toast.error('Payment system is not configured. Please contact support.');
      } else {
        toast.error(errorMessage);
      }

      setProcessingTier(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
        <p className="text-lg text-gray-600">
          Select the plan that best fits your career development needs
        </p>
        {!hasSetupFee && (
          <div className="mt-4 inline-block bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <p className="text-sm text-blue-800">
              <strong>Step 1 of 2:</strong> One-time setup fee of $197 required for all plans
            </p>
          </div>
        )}
        {hasSetupFee && !hasActiveSubscription && (
          <div className="mt-4 inline-block bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <p className="text-sm text-green-800">
              <strong>Step 2 of 2:</strong> Setup fee paid! Now select your monthly subscription plan
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {tiers.map((tier) => {
          const isCurrentTier = currentTier !== null && tier.slug === currentTier;
          const isProcessing = processingTier === tier.slug;

          return (
            <div
              key={tier.id}
              className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${
                tier.slug === 'growth' ? 'ring-2 ring-blue-600' : ''
              }`}
            >
              {tier.slug === 'growth' && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm font-semibold">
                  Popular
                </div>
              )}

              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                {tier.description && (
                  <p className="text-gray-600 text-sm mb-4">{tier.description}</p>
                )}

                <div className="mb-6">
                  <div className="text-sm text-gray-600 mb-2">
                    One-time setup: <span className="font-semibold text-gray-900">$197</span>
                  </div>
                  <div>
                    <span className="text-4xl font-bold text-gray-900">
                      ${(tier.monthly_price / 100).toFixed(0)}
                    </span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  {tier.annual_price && (
                    <div className="text-sm text-gray-600 mt-1">
                      or ${(tier.annual_price / 100).toFixed(0)}/year (save{' '}
                      {Math.round(((tier.monthly_price * 12 - tier.annual_price) / (tier.monthly_price * 12)) * 100)}
                      %)
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Zap className="text-blue-600 mr-2 flex-shrink-0" size={20} />
                    <span className="text-sm text-gray-700">
                      {tier.quota_limits.report_runs} report runs per month
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Zap className="text-blue-600 mr-2 flex-shrink-0" size={20} />
                    <span className="text-sm text-gray-700">
                      {tier.quota_limits.ai_coach_messages} AI coach messages per month
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Zap className="text-blue-600 mr-2 flex-shrink-0" size={20} />
                    <span className="text-sm text-gray-700">
                      {tier.quota_limits.job_match_analyses} job match analyses per month
                    </span>
                  </li>
                </ul>

                <div className="border-t border-gray-200 pt-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Features Included:</h4>
                  <ul className="space-y-2">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="text-green-600 mr-2 flex-shrink-0" size={18} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleSelectTier(tier.slug)}
                  disabled={isCurrentTier || isProcessing}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    isCurrentTier
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : tier.slug === 'growth'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isProcessing
                    ? 'Processing...'
                    : isCurrentTier
                    ? 'Current Plan'
                    : hasSetupFee && !hasActiveSubscription
                    ? 'Subscribe Now'
                    : hasSetupFee && hasActiveSubscription
                    ? 'Select Plan'
                    : 'Get Started'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
